import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, EmptyState, Badge } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import SchoolFilter, { useSchools } from "@/components/SchoolFilter";
import DateField from "@/components/DateField";
import { OPP_STATUS, OPP_STATUS_COLORS, OPP_VOL_TYPE } from "@/lib/constants";
import { HandHeart, Plus, Search, Pencil, Trash2, Eye, Users, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";
import { OpportunityParticipants } from "@/components/OpportunityParticipants";

const empty = {
  name: "", start_date: "", end_date: "", location: "",
  volunteer_type: "both", level: "عام", seats: 0, credit_hours: 0, expected_beneficiaries: 0,
  status: "open", evidence_image: "", closing_image: "", closing_report_image: "", school_id: "",
};

const OPP_LEVELS = {
  "عام": "عام (28 ر.س/ساعة)",
  "مهاري": "مهاري (45.55 ر.س/ساعة)",
  "احترافي": "احترافي (45.55 ر.س/ساعة)",
};
const levelRate = (l) => (l === "عام" ? 28 : 45.55);

// Compress any image to a JPEG data-url under maxKB (default 200KB)
const compressImage = (file, maxKB = 200) => new Promise((resolve, reject) => {
  if (!file.type.startsWith("image/")) return reject(new Error("الملف ليس صورة"));
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("تعذّر قراءة الصورة"));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error("صورة غير صالحة"));
    img.onload = () => {
      let { width, height } = img;
      const maxDim = 1400;
      if (Math.max(width, height) > maxDim) {
        const s = maxDim / Math.max(width, height);
        width = Math.round(width * s); height = Math.round(height * s);
      }
      const canvas = document.createElement("canvas");
      const draw = () => { canvas.width = width; canvas.height = height; canvas.getContext("2d").drawImage(img, 0, 0, width, height); };
      const sizeKB = (u) => (u.length * 3) / 4 / 1024;
      draw();
      let q = 0.85;
      let out = canvas.toDataURL("image/jpeg", q);
      while (sizeKB(out) > maxKB && q > 0.3) { q -= 0.1; out = canvas.toDataURL("image/jpeg", q); }
      let tries = 0;
      while (sizeKB(out) > maxKB && tries < 5) {
        width = Math.round(width * 0.8); height = Math.round(height * 0.8);
        draw(); out = canvas.toDataURL("image/jpeg", 0.6); tries++;
      }
      if (sizeKB(out) > maxKB) return reject(new Error("تعذّر ضغط الصورة إلى أقل من 200 كيلوبايت — اختر صورة أصغر"));
      resolve(out);
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

// Read a PDF file as a data-url (no compression), guarded by a max size in KB
const readPdf = (file, maxKB = 3072) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("تعذّر قراءة الملف"));
  reader.onload = () => {
    const sizeKB = (reader.result.length * 3) / 4 / 1024;
    if (sizeKB > maxKB) return reject(new Error(`حجم ملف PDF كبير — يجب أن يكون أقل من ${Math.round(maxKB / 1024)} ميجابايت`));
    resolve(reader.result);
  };
  reader.readAsDataURL(file);
});

const isPdf = (v) => typeof v === "string" && v.startsWith("data:application/pdf");

const F = ({ label, children }) => (
  <div><label className="text-xs text-stone-500 mb-1 block">{label}</label>{children}</div>
);
const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";

export default function Opportunities() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const schools = useSchools();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [schoolF, setSchoolF] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [view, setView] = useState(null);
  const [imgBusy, setImgBusy] = useState("");
  const [participantsOpp, setParticipantsOpp] = useState(null);

  const load = () => {
    const qp = isAdmin && schoolF ? `?school_id=${schoolF}` : "";
    api.get(`/opportunities${qp}`).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(load, [schoolF, isAdmin]);

  const filtered = useMemo(() => items.filter((o) =>
    (!q || o.name?.includes(q) || o.field?.includes(q) || o.number?.includes(q)) &&
    (!statusF || o.status === statusF)
  ), [items, q, statusF]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (o) => { setForm({ ...empty, ...o }); setEditId(o.id); setOpen(true); };
  const onImg = (key) => async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImgBusy(key);
    try {
      const isPdfFile = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
      const data = isPdfFile ? await readPdf(f, 3072) : await compressImage(f, 200);
      set(key, data);
    }
    catch (err) { toast.error(err.message || "تعذّر معالجة الملف"); }
    finally { setImgBusy(""); }
  };

  const save = async () => {
    if (isAdmin && !editId && !form.school_id) return toast.error("اختر المدرسة");
    const req = [
      [form.name, "اسم الفرصة"], [form.location, "مكان التنفيذ"],
      [form.start_date, "تاريخ البداية"], [form.end_date, "تاريخ النهاية"],
      [form.level, "مستوى الفرصة"],
    ];
    for (const [v, label] of req) if (!v || !String(v).trim()) return toast.error(`الحقل مطلوب: ${label}`);
    if (!(+form.seats > 0)) return toast.error("عدد المقاعد مطلوب");
    if (!(+form.credit_hours > 0)) return toast.error("عدد الساعات مطلوب");
    if (+form.credit_hours > 50) return toast.error("لا يمكن أن يزيد عدد ساعات الفرصة الواحدة عن 50 ساعة");
    if (!(+form.expected_beneficiaries > 0)) return toast.error("عدد المتطوعين في الفرصة مطلوب");
    if (+form.expected_beneficiaries > 50) return toast.error("لا يمكن أن يزيد عدد المتطوعين في الفرصة الواحدة عن 50 متطوعاً");
    if (!form.evidence_image) return toast.error("صورة شاهد بيانات الفرصة مطلوبة");
    if (!form.closing_image) return toast.error("صورة إغلاق الفرصة مطلوبة");
    if (!form.closing_report_image) return toast.error("صورة تقرير ختام الفرصة (موقّع ومختوم) مطلوبة");
    try {
      const payload = { ...form, seats: +form.seats, credit_hours: +form.credit_hours, expected_beneficiaries: +form.expected_beneficiaries };
      if (editId) await api.put(`/opportunities/${editId}`, payload);
      else await api.post("/opportunities", payload);
      playSuccess();
      toast.success(editId ? "تم التحديث" : "تمت إضافة الفرصة");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async () => {
    try { await api.delete(`/opportunities/${delId}`); toast.success("تم الحذف"); setDelId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <InspireBanner />
      <PageTitle title="الفرص التطوعية" subtitle="كل فرصة تفتحها… بابٌ لأجرٍ ومجدٍ لمدرستك" icon={HandHeart}
        actions={!isAdmin && <button data-testid="add-opportunity-btn" onClick={openNew} className="btn-gold px-4 py-2 rounded-xl flex items-center gap-2"><Plus size={18} /> فرصة جديدة</button>}
      />

      <div className="glass rounded-2xl p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
          <input data-testid="opp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث..." className={`${inputCls} pr-10`} />
        </div>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className={inputCls + " max-w-[160px]"}>
          <option value="">كل الحالات</option>
          {Object.entries(OPP_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {isAdmin && <SchoolFilter value={schoolF} onChange={setSchoolF} schools={schools} />}
      </div>

      {!filtered.length ? <EmptyState text="لا توجد فرص تطوعية" icon={HandHeart} /> : (
        <div className="glass rounded-2xl p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-stone-500 text-right border-b border-amber-400/15">
                <th className="py-3 px-2 font-medium">اسم الفرصة</th>
                <th className="py-3 px-2 font-medium">المستوى</th>
                <th className="py-3 px-2 font-medium">البداية</th>
                <th className="py-3 px-2 font-medium">المقاعد</th>
                <th className="py-3 px-2 font-medium">الساعات</th>
                <th className="py-3 px-2 font-medium">المشاركون</th>
                <th className="py-3 px-2 font-medium">الساعات الفعلية</th>
                <th className="py-3 px-2 font-medium">الحالة</th>
                <th className="py-3 px-2 font-medium">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <motion.tr key={o.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-b border-amber-400/10 hover:bg-amber-100/50 transition">
                  <td className="py-3 px-2 text-stone-800 font-medium">{o.name}</td>
                  <td className="py-3 px-2 text-stone-600">{o.level || "عام"}</td>
                  <td className="py-3 px-2 text-stone-600">{o.start_date || "-"}</td>
                  <td className="py-3 px-2 text-stone-600">{o.seats}</td>
                  <td className="py-3 px-2 text-stone-600">{o.credit_hours}</td>
                  <td className="py-3 px-2"><span data-testid={`opp-participants-${o.id}`} className="inline-flex items-center gap-1 text-sky-700 font-medium" title="عدد الطلاب المسجّلين فعلياً في الفرصة">{o.participants_count || 0}{o.seats_planned ? ` / ${o.seats_planned}` : ""}</span></td>
                  <td className="py-3 px-2"><span data-testid={`opp-logged-hours-${o.id}`} className="inline-flex items-center gap-1 text-amber-600 font-semibold" title="مجموع ساعات المشاركين المنجزين">{o.logged_hours || 0}</span></td>
                  <td className="py-3 px-2"><Badge className={OPP_STATUS_COLORS[o.status]}>{OPP_STATUS[o.status]}</Badge></td>
                  <td className="py-3 px-2">
                    <div className="flex gap-1">
                      <button onClick={() => setView(o)} className="p-1.5 rounded-lg hover:bg-amber-500/20 text-amber-600"><Eye size={16} /></button>
                      <button data-testid={`manage-participants-${o.id}`} onClick={() => setParticipantsOpp(o)} className="p-1.5 rounded-lg hover:bg-sky-500/20 text-sky-600" title="كشف المشاركين واحتساب الساعات"><Users size={16} /></button>
                      {!isAdmin && <>
                        <button data-testid={`edit-opp-${o.id}`} onClick={() => openEdit(o)} className="p-1.5 rounded-lg hover:bg-amber-500/20 text-amber-600"><Pencil size={16} /></button>
                        <button data-testid={`del-opp-${o.id}`} onClick={() => setDelId(o.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-700"><Trash2 size={16} /></button>
                      </>}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">{editId ? "تعديل الفرصة" : "فرصة تطوعية جديدة"}</DialogTitle></DialogHeader>
          <p className="text-[11px] text-amber-700 -mt-2 mb-1">جميع الحقول والمرفقات إلزامية. يمكن رفع صورة (بأي صيغة، تُضغط تلقائياً لأقل من 200 كيلوبايت) أو ملف PDF (حتى 3 ميجابايت).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isAdmin && !editId && (
              <F label="المدرسة"><select data-testid="opp-school" value={form.school_id} onChange={(e) => set("school_id", e.target.value)} className={inputCls}><option value="">اختر</option>{schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></F>
            )}
            <F label="اسم الفرصة"><input data-testid="opp-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></F>
            <F label="مستوى الفرصة"><select data-testid="opp-level" value={form.level} onChange={(e) => set("level", e.target.value)} className={inputCls}>{Object.entries(OPP_LEVELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></F>
            <F label="مكان التنفيذ"><input data-testid="opp-location" value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} /></F>
            <F label="تاريخ البداية"><DateField testId="opp-start" value={form.start_date} onChange={(v) => set("start_date", v)} /></F>
            <F label="تاريخ النهاية"><DateField testId="opp-end" value={form.end_date} onChange={(v) => set("end_date", v)} /></F>
            <F label="نوع المتطوع"><select value={form.volunteer_type} onChange={(e) => set("volunteer_type", e.target.value)} className={inputCls}>{Object.entries(OPP_VOL_TYPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></F>
            <F label="عدد المقاعد"><input data-testid="opp-seats" type="number" value={form.seats} onChange={(e) => set("seats", e.target.value)} className={inputCls} /></F>
            <F label="عدد الساعات (بحد أقصى 50)"><input data-testid="opp-hours" type="number" min="1" max="50" value={form.credit_hours} onChange={(e) => set("credit_hours", e.target.value)} className={inputCls} /></F>
            <F label="عدد المتطوعين في هذه الفرصة (بحد أقصى 50)"><input data-testid="opp-benef" type="number" min="1" max="50" value={form.expected_beneficiaries} onChange={(e) => set("expected_beneficiaries", e.target.value)} className={inputCls} /></F>
            <F label="الحالة"><select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>{Object.entries(OPP_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></F>
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[["صورة شاهد من بيانات الفرصة في منصة التطوع", "evidence_image"], ["صورة بعد إغلاق الفرصة", "closing_image"], ["صورة تقرير ختام الفرصة (موقّع ومختوم)", "closing_report_image"]].map(([label, k]) => (
                <F key={k} label={label}>
                  <p className="text-[10px] text-stone-400 mb-1">الصيغ المقبولة: JPG، PNG، WEBP، أو PDF</p>
                  <input data-testid={`opp-${k}`} type="file" accept="image/*,application/pdf" onChange={onImg(k)} className={inputCls} />
                  {imgBusy === k && <p className="text-xs text-sky-600 mt-1">جارٍ المعالجة...</p>}
                  {form[k] && (isPdf(form[k])
                    ? <a href={form[k]} target="_blank" rel="noreferrer" data-testid={`opp-${k}-pdf`} className="mt-2 flex items-center gap-1 text-red-600 text-xs underline"><FileText size={14} /> عرض ملف PDF</a>
                    : <img src={form[k]} alt={label} className="mt-2 w-full h-28 object-cover rounded-lg" />)}
                </F>
              ))}
            </div>
          </div>
          <DialogFooter><button data-testid="save-opp-btn" onClick={save} className="btn-gold px-6 py-2 rounded-xl">حفظ</button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View */}
      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-lg" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">{view?.name}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-2 text-sm">
              {[["المستوى", view.level || "عام"], ["سعر الساعة", `${levelRate(view.level)} ر.س`], ["المكان", view.location], ["البداية", view.start_date], ["النهاية", view.end_date], ["المقاعد", view.seats], ["الساعات", view.credit_hours], ["المشاركون", view.participants_count || 0], ["الساعات المسجّلة", view.logged_hours || 0], ["عدد المتطوعين في الفرصة", view.expected_beneficiaries], ["الحالة", OPP_STATUS[view.status]]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-amber-400/10 py-1.5"><span className="text-stone-500">{k}</span><span className="text-stone-800">{v || "-"}</span></div>
              ))}
              <div className="grid grid-cols-3 gap-2 pt-2">
                {[["evidence_image", "شاهد"], ["closing_image", "إغلاق"], ["closing_report_image", "تقرير الختام"]].map(([k, alt]) => view[k] && (
                  isPdf(view[k])
                    ? <a key={k} href={view[k]} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center gap-1 h-20 rounded-lg border border-red-300 bg-red-50 text-red-600 text-[11px]"><FileText size={20} /> {alt} (PDF)</a>
                    : <img key={k} src={view[k]} alt={alt} className="w-full h-20 object-cover rounded-lg" />
                ))}
              </div>
              {view.description && <div className="pt-2"><span className="text-stone-500">الوصف: </span>{view.description}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader><AlertDialogTitle className="text-stone-800">تأكيد الحذف</AlertDialogTitle><AlertDialogDescription className="text-stone-500">سيتم حذف الفرصة نهائياً.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-amber-50 border-amber-400/30 text-stone-800">إلغاء</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-del-opp" onClick={remove} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {participantsOpp && (
        <OpportunityParticipants opp={participantsOpp} onClose={() => setParticipantsOpp(null)} onChanged={load} />
      )}
    </div>
  );
}
